import { useEffect, useState } from 'react';

import type { SiteStats } from '@/api/client';
import { wikiApiClient } from '@/api/client';
import { useT } from '@/i18n/LocaleProvider';

export function Stats() {
  const t = useT();
  const [stats, setStats] = useState<SiteStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    wikiApiClient
      .getStats()
      .then((result: SiteStats) => {
        if (cancelled) return;
        setStats(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(errorMessage(err, t('stats_error_load')));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <article className="note" data-testid="site-stats">
      <header className="note-header">
        <div className="note-kicker">
          <span>{t('nav_stats')}</span>
        </div>
        <h1>{t('stats_title')}</h1>
      </header>

      {isLoading ? <div className="empty-note">{t('stats_loading')}</div> : null}
      {!isLoading && error ? <div className="empty-note">{error}</div> : null}

      {!isLoading && !error && stats ? (
        <table className="stats-table" data-testid="stats-table">
          <tbody>
            <tr>
              <th scope="row">{t('stats_published_pages')}</th>
              <td>{stats.published_pages}</td>
            </tr>
            <tr>
              <th scope="row">{t('stats_stub_pages')}</th>
              <td>{stats.stub_pages}</td>
            </tr>
            <tr>
              <th scope="row">{t('stats_total_revisions')}</th>
              <td>{stats.total_revisions}</td>
            </tr>
            <tr>
              <th scope="row">{t('stats_images')}</th>
              <td>{stats.images}</td>
            </tr>
            <tr>
              <th scope="row">{t('stats_categories')}</th>
              <td>{stats.categories}</td>
            </tr>
          </tbody>
        </table>
      ) : null}

      {!isLoading && !error && stats && Object.keys(stats.accounts_by_role).length > 0 ? (
        <section data-testid="accounts-by-role">
          <h2>{t('stats_accounts_by_role')}</h2>
          <table className="stats-table">
            <tbody>
              {Object.entries(stats.accounts_by_role)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([role, count]) => (
                  <tr key={role}>
                    <th scope="row">{role}</th>
                    <td>{count}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </article>
  );
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
